export const ATTACHMENT_STATUSES = ["pending", "ready", "failed"];
export const WORKSPACE_SEARCH_TYPES = [
    "messages",
    "pages",
    "tickets",
    "contacts",
    "events",
];
export const WORK_ITEM_PAGE_ROLES = [
    "proposal",
    "plan",
    "outcome",
    "reference",
    // The Project brief (ADR-0024, "Substrate note"): "A Project brief is that
    // pattern with a `brief` role and a `commission_project` gate kind." It binds
    // to the Project's scoping work item, because `work_item_pages` is keyed on a
    // work item, and a second page substrate keyed on a Project would have to
    // re-derive the revisioning, the tenancy and the gate wiring this one already
    // carries.
    "brief",
];
/**
 * The per-milestone gate kind, named so the role map and the render wiring say
 * what they mean instead of repeating a string literal.
 *
 * ADR-0024 §After the push: "the commissioning gate is per-milestone-
 * decomposition, not per-Project… each milestone's tickets still resolve their
 * own gate before they leave." That gate rides the SAME role-'brief' page as
 * commissioning, which is why `PAGE_ROLE_GATE_KIND` maps a role to a LIST.
 *
 * Typed as `WorkItemGateKind`: the union is a claim about what the database
 * accepts, and the CHECK widening ships alongside it. (It was a bare string
 * while the push lived on a separate branch, deliberately, so that wave never
 * asserted the database took a value it had not yet been taught.)
 */
export const MILESTONE_DECOMPOSITION_GATE_KIND = "milestone_decomposition";
/**
 * The review-queue title prefixes Reeve's reads are filed under. The worker
 * output page splits Reeve's record on `changesRequested`, and the backend
 * title builders (plan and proposal reads) compose from the same pair — one
 * constant so the split and the titles cannot drift apart.
 */
export const REEVE_READ_TITLE_PREFIXES = {
    changesRequested: "Requested changes",
    approved: "Approved",
};
/**
 * The ONE map from a bound page's role to the gate kinds that sign it off.
 *
 * Three consumers read it and none of them owns it: `workItems/gates.ts`
 * inverts it into `PINNED_GATE_ROLES` (which revision a resolution pins),
 * `workItems/pageBinding.ts` compiles it into the SQL that attaches the right
 * gate to the right page, and the Library review header keys its verbs off it.
 * Before this existed the same pairing was written out three times, so widening
 * it to a new role meant finding all three sites and getting all three right.
 *
 * The values are LISTS because a role is not limited to one decision. A Project
 * brief carries the commissioning gate AND, once wave 4 lands, the per-milestone
 * decomposition gate: the same document, reviewed twice for different reasons.
 * A one-to-one map would have forced the second gate onto a second page role and
 * split a Project's argument history across two documents, which is exactly what
 * "re-scoping is a new page revision, not a new Project" forbids.
 *
 * A role absent from this map is a page nobody signs off: 'outcome' is a record
 * of what happened and 'reference' is context, and neither has a decision
 * attached. Absence is the statement, so the map is deliberately partial.
 */
export const PAGE_ROLE_GATE_KIND = {
    proposal: ["accept_proposal"],
    plan: ["approve_execution_plan"],
    // The Project brief (ADR-0024 step 4): "Commissioning is the gate, and
    // resolving it pins the exact revision." Then, per milestone, the tickets
    // still resolve their own gate before they leave (§After the push).
    brief: ["commission_project", MILESTONE_DECOMPOSITION_GATE_KIND],
};
export const REVIEW_PAGE_ROLES = Object.keys(PAGE_ROLE_GATE_KIND);
/** Narrowing guard so a role string off the wire reaches the map safely. */
export function isReviewPageRole(role) {
    return Object.prototype.hasOwnProperty.call(PAGE_ROLE_GATE_KIND, role);
}
/**
 * Lifecycle status of a desk-rhythm run (`desk_rhythm_runs.status`). 'succeeded'
 * matches the table CHECK constraint. (The retired workspace-cadence table used
 * 'success'; both it and its status enum were dropped in 0136.)
 */
export const DESK_RHYTHM_RUN_STATUSES = ["running", "succeeded", "failed", "skipped"];
//# sourceMappingURL=content.js.map